// Desktop (>= 900px) request-workspace table for `/russia-rates`. Uses
// the shared `WorkspaceTable` shell (sticky uppercase 10-px headers,
// CSS-grid columns) and `RussiaRequestRow` for full-row hover/click/
// keyboard activation. URGENT rows render with a `--status-error-bg`
// tint so the user can scan for urgent requests at a glance.

import { useTranslation } from 'react-i18next';

import { WorkspaceTable, type WorkspaceTableColumn } from '../../../ui/index.js';
import type { RussiaRequest } from '@magimp/shared';
import { RussiaRequestRow } from './RussiaRequestRow.js';

// WHY: column widths track the existing Russia field rhythm — the ID,
// type, status, manager, and created date stay compact while name,
// start-cities, end-address, and primary-bid flex with content. No
// horizontal overflow at the 900px narrow boundary because the four
// flex columns each carry `minmax(0,…)` semantics in the cell content.
const GRID_TEMPLATE =
  '110px 100px 110px minmax(220px,1.4fr) 140px minmax(160px,1fr) minmax(180px,1.2fr) minmax(160px,1fr) 110px';

export function RussiaRequestsListTable({
  rows,
  language,
  onOpenRow,
  notificationCountFor,
}: {
  rows: readonly RussiaRequest[];
  language: string;
  onOpenRow: (id: string) => void;
  notificationCountFor?: (id: string) => number;
}) {
  const { t } = useTranslation('russiaRequests');
  const columns: readonly WorkspaceTableColumn[] = [
    { key: 'id', label: t('columns.id') },
    { key: 'type', label: t('columns.type') },
    { key: 'status', label: t('columns.status') },
    { key: 'name', label: t('columns.name') },
    { key: 'manager', label: t('columns.manager') },
    { key: 'startCities', label: t('columns.startCities') },
    { key: 'endAddress', label: t('columns.endAddress') },
    { key: 'primaryBidSummary', label: t('columns.primaryBidSummary') },
    { key: 'createdAt', label: t('columns.createdAt') },
  ];
  return (
    <div
      data-testid="russia-requests-list-table"
      style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
    >
      <WorkspaceTable columnsTemplate={GRID_TEMPLATE} columns={columns}>
        {rows.map((row) => (
          <RussiaRequestRow
            key={row.id}
            row={row}
            onOpen={() => onOpenRow(row.id)}
            language={language}
            {...(notificationCountFor ? { notificationCount: notificationCountFor(row.id) } : {})}
          />
        ))}
      </WorkspaceTable>
    </div>
  );
}
